import React, { useMemo, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import Map from './Map';
import ZoomableImage from './ZoomableImage';
import { config } from '../utils/config';
import { extractDeviceType } from '../utils/device';
import zooMap from '@/assets/taipei-zoo-v2.jpg';
import { buildWorstStatusMap } from '../utils/alertStatus';

const defaultCenter = {
    lat: 24.877466,
    lng: 121.247505,
};

function DashboardMap({ focusDeviceName }) {
    const { alertData, devicesLoc } = useOutletContext() || {};
    const devicesLocData = useMemo(() => devicesLoc || [], [devicesLoc]);
    const alertStatusData = useMemo(() => alertData || [], [alertData]);
    const projectType = config.appType || 'default';
    const navigate = useNavigate();
    const statusByName = useMemo(
        () =>
            buildWorstStatusMap(alertStatusData, {
                getKey: (alert) => alert?.device_name,
            }),
        [alertStatusData]
    );

    const toNumber = (value) => {
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    };

    const isValidLatLng = (lat, lng) =>
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180;

    const getDeviceName = (device) =>
        device?.name || device?.device_name || device?.deviceName || '未命名設備';

    const getDevicePosition = (device) => {
        if (!device) return null;

        const lat = toNumber(device.lat) ?? toNumber(device.latitude);
        const lng =
            toNumber(device.lng) ?? toNumber(device.longitude) ?? toNumber(device.lon);

        if (!isValidLatLng(lat, lng)) return null;

        return { lat, lng };
    };

    const handleMarkerClick = useCallback(
        (deviceName) => {
            if (!deviceName) return;
            navigate(`/device/${deviceName}`);
        },
        [navigate]
    );

    const mapMarkers = useMemo(() => {
        if (!Array.isArray(devicesLocData)) return [];

        return devicesLocData
            .map((device) => {
                const position = getDevicePosition(device);
                if (!position) return null;

                const name = getDeviceName(device);
                const type =
                    device.type ||
                    device.device_type ||
                    extractDeviceType(name) ||
                    'default';
                const status = statusByName.get(name) || 'default';

                return {
                    id: device.id ?? `${name}-${position.lat}-${position.lng}`,
                    position,
                    name,
                    type,
                    status,
                    onClick: () => handleMarkerClick(name),
                };
            })
            .filter(Boolean);
    }, [devicesLocData, alertStatusData, handleMarkerClick]);

    const markersWithOffsets = useMemo(() => {
        const threshold = 0.00015;
        const tailVariants = [90, 150, 190];

        return mapMarkers.map((marker, index, list) => {
            const overlapCount = list.slice(0, index).filter((other) => {
                const latDiff = Math.abs(other.position.lat - marker.position.lat);
                const lngDiff = Math.abs(other.position.lng - marker.position.lng);
                return latDiff < threshold && lngDiff < threshold;
            }).length;

            const tailLength = tailVariants[overlapCount % tailVariants.length];

            return {
                ...marker,
                tailLength,
            };
        });
    }, [mapMarkers]);

    const focusPosition = useMemo(() => {
        if (!focusDeviceName || !Array.isArray(devicesLocData)) return null;
        const target = devicesLocData.find(
            (device) => getDeviceName(device) === focusDeviceName
        );
        return getDevicePosition(target);
    }, [focusDeviceName, devicesLocData]);

    const mapCenter = focusPosition ?? defaultCenter;
    const enableZooCoordinateDebug =
        projectType === 'zoo' &&
        (import.meta.env.DEV || config?.env === 'development');

    const mapRegistry = {
        default: () => (
            <Map
                apiKey={config.GOOGLE_MAP_API_KEY}
                center={mapCenter}
                markers={markersWithOffsets}
            />
        ),
        zoo: () => (
            <ZoomableImage
                image={zooMap}
                defaultCenter={{ x: 2380, y: 1400 }}
                deviceLoc={devicesLocData}
                alertStatus={alertStatusData}
                statusByName={statusByName}
                enableCoordinateDebug={enableZooCoordinateDebug}
            />
        ),
    };

    return mapRegistry[projectType]?.() ?? null;
}

export default DashboardMap;
